import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Play, AlertCircle } from 'lucide-react'
import api from '../services/api'

interface VideoInfo {
  video: {
    title: string
    thumbnail_url?: string
    duration?: number
    embed_url: string
  }
  link: {
    custom_message?: string
    custom_cta_text?: string
    custom_cta_url?: string
  }
}

export default function PublicView() {
  const { code } = useParams<{ code: string }>()
  const [videoInfo, setVideoInfo] = useState<VideoInfo | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    if (code) {
      loadVideoInfo()
    }
  }, [code])

  const loadVideoInfo = async () => {
    try {
      const response = await api.get(`/v/${code}/info`)
      setVideoInfo(response.data)
    } catch (err: unknown) {
      const error = err as { response?: { status?: number; data?: { error?: string } } }
      if (error.response?.status === 404) {
        setError('Video not found')
      } else if (error.response?.status === 403) {
        setError('This video is no longer available')
      } else {
        setError('Failed to load video')
      }
    } finally {
      setIsLoading(false)
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Oops!</h1>
          <p className="text-slate-400 mb-6">{error}</p>
          <a href="/" className="btn-primary">
            Go Home
          </a>
        </div>
      </div>
    )
  }

  if (!videoInfo) {
    return null
  }

  const { video, link } = videoInfo

  return (
    <div className="min-h-screen bg-slate-900 flex flex-col">
      {/* Header */}
      <header className="h-16 border-b border-slate-700 flex items-center px-6">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center">
            <Play className="w-5 h-5 text-white" />
          </div>
          <span className="text-lg font-bold text-white">CapyWatch</span>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 flex flex-col items-center justify-center p-4 md:p-8">
        <div className="w-full max-w-4xl space-y-6">
          {/* Video player */}
          <div className="relative aspect-video bg-slate-800 rounded-xl overflow-hidden shadow-2xl">
            <iframe
              src={`${video.embed_url}?autoplay=true`}
              allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="absolute inset-0 w-full h-full border-0"
            />
          </div>

          {/* Video info */}
          <div className="space-y-4">
            <h1 className="text-2xl md:text-3xl font-bold text-white">{video.title}</h1>

            {link.custom_message && (
              <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
                <p className="text-slate-300 whitespace-pre-wrap">{link.custom_message}</p>
              </div>
            )}

            {link.custom_cta_url && (
              <a
                href={link.custom_cta_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-primary-600 hover:bg-primary-500 text-white font-medium px-6 py-3 rounded-lg transition-colors"
              >
                {link.custom_cta_text || 'Learn More'}
              </a>
            )}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-4 text-center text-slate-500 text-sm">
        Powered by <span className="text-primary-400">CapyWatch</span>
      </footer>
    </div>
  )
}
